import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import AccommodationCalendarDayPicker from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarDayPicker'
import type {
  AccommodationDayPickerSize,
  AccommodationMobileDayCellSelectHandler,
} from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import type { AccommodationDayCellVariant } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarLargeDayCell'
import noop from 'lib/function/noop'
import { generateStaticDayViews } from 'lib/datetime/calendarDayViews'

interface Props {
  /** Destination time zone. Determines earliest permissible check-in date. */
  propertyTimezone?: string
  checkInDate?: string
  checkOutDate?: string
  /**
   * Reference to the scroll container element this picker sits in
   * This is necessary to provide so it can manage showing the correct month
   */
  scrollerRef: React.RefObject<HTMLElement | null>
  size: AccommodationDayPickerSize
  /**
   * The month we're picking the date for inso ISO date format
   */
  selectedMonth?: string

  /**
   * Event for when the selected dates change
   */
  onDatesChange?: AccommodationMobileDayCellSelectHandler

  availabilityVariantMap?: Map<string, AccommodationDayCellVariant>
  renderFromStart?: boolean
}

function OfferDatePicker({
  propertyTimezone,
  checkInDate,
  scrollerRef,
  checkOutDate,
  size,
  selectedMonth,
  onDatesChange = noop,
  availabilityVariantMap,
  renderFromStart,
}: Props) {
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onDatesSelect = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    onDatesChange(day)
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.DateChanged)
  }, [onDatesChange, onUserAnalyticsAction])

  const month = useMemo(() => selectedMonth ? new Date(selectedMonth) : undefined, [selectedMonth])

  useEffect(() => {
    if (!month) {
      scrollerRef.current?.scrollTo({ behavior: 'auto', top: 0 })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [month])

  const staticDayViews = useMemo(() => generateStaticDayViews(propertyTimezone), [propertyTimezone])

  return <AccommodationCalendarDayPicker
    key={month ? `${month.getFullYear()}-${month.getMonth()}` : 'default'}
    startDate={checkInDate}
    endDate={checkOutDate}
    onDaySelect={onDatesSelect}
    contentRef={scrollerRef}
    views={staticDayViews}
    initialMonth={month?.getMonth()}
    initialYear={month?.getFullYear()}
    size={size}
    availabilityVariantMap={availabilityVariantMap}
    renderFromStart={renderFromStart}
  />
}

export default withOfferPageState(state => ({
  open: !!state.activeFilter || state.activeHeaderFilter === 'checkin',
}), OfferDatePicker, preventRenderWithClosedFilter)
